import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiGet, apiDelete, apiPost, apiPut } from '../services/api';
import InviteUserModal from '../components/InviteUserModal';
import {
  Plus,
  Search,
  UserCircle,
  Mail,
  FolderKanban,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  UserX,
  UserCheck,
  Send,
  Clock,
  CheckCircle,
  XCircle,
} from 'lucide-react';

interface User {
  id: string;
  username: string;
  email: string;
  full_name: string | null;
  is_active: boolean;
  role: string;
  last_login: string | null;
  project_count: number;
}

interface Invitation {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
  status: string;
  expires_at: string;
  inviter_name: string | null;
  created_at: string;
}

interface PaginatedResponse {
  items: User[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export default function Users() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState<User[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'users' | 'invitations'>('users');

  const page = parseInt(searchParams.get('page') || '1');

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('page_size', '20');
      if (search) params.set('search', search);

      const data = await apiGet<PaginatedResponse>(`/api/users?${params}`);
      setUsers(data.items);
      setTotalPages(data.total_pages);
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchInvitations = async () => {
    try {
      const data = await apiGet<Invitation[]>('/api/users/invitations');
      setInvitations(data);
    } catch (err) {
      console.error('Failed to fetch invitations:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchInvitations();
  }, [page, search]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    if (search) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  };

  const handleDeactivate = async (userId: string) => {
    if (!confirm('Are you sure you want to deactivate this user?')) return;
    try {
      await apiDelete(`/api/users/${userId}`);
      fetchUsers();
    } catch (err) {
      console.error('Failed to deactivate user:', err);
    }
    setActionMenuId(null);
  };

  const handleActivate = async (userId: string) => {
    try {
      await apiPut(`/api/users/${userId}`, { is_active: true });
      fetchUsers();
    } catch (err) {
      console.error('Failed to activate user:', err);
    }
    setActionMenuId(null);
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      await apiPost(`/api/users/invitations/${invitationId}/resend`);
      fetchInvitations();
    } catch (err) {
      console.error('Failed to resend invitation:', err);
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    if (!confirm('Cancel this invitation?')) return;
    try {
      await apiDelete(`/api/users/invitations/${invitationId}`);
      fetchInvitations();
    } catch (err) {
      console.error('Failed to cancel invitation:', err);
    }
  };

  const statusIcon = (inv: Invitation) => {
    switch (inv.status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-400" />;
      case 'accepted':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'expired':
        return <XCircle className="w-4 h-4 text-gray-500" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-red-400" />;
      default:
        return null;
    }
  };

  const pendingInvitations = invitations.filter((i) => i.status === 'pending');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Users</h1>
          <p className="text-gray-400">{total} total users</p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
        >
          <Plus className="w-5 h-5" />
          Invite User
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-dark-900 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'users'
              ? 'bg-dark-700 text-white'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Users ({total})
        </button>
        <button
          onClick={() => setActiveTab('invitations')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'invitations'
              ? 'bg-dark-700 text-white'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Invitations {pendingInvitations.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 rounded-full text-xs">
              {pendingInvitations.length}
            </span>
          )}
        </button>
      </div>

      {activeTab === 'users' && (
        <>
          {/* Search */}
          <form onSubmit={handleSearch}>
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search users..."
                className="w-full pl-10 pr-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </form>

          {/* Users Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {isLoading ? (
              <div className="col-span-full p-8 text-center text-gray-500">Loading...</div>
            ) : users.length === 0 ? (
              <div className="col-span-full p-8 text-center text-gray-500">No users found</div>
            ) : (
              users.map((user) => (
                <div
                  key={user.id}
                  className={`bg-dark-900 rounded-xl border p-4 relative ${
                    user.is_active ? 'border-dark-800' : 'border-dark-800/50 opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                        <span className="text-primary-500 font-semibold">
                          {user.full_name?.charAt(0) || user.username.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <Link
                          to={`/admin/users/${user.id}`}
                          className="font-medium text-white hover:text-primary-500"
                        >
                          {user.full_name || user.username}
                        </Link>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            user.role === 'admin'
                              ? 'bg-purple-500/20 text-purple-400'
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {user.role}
                          </span>
                          {!user.is_active && (
                            <span className="text-xs text-gray-500">(Inactive)</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="relative">
                      <button
                        onClick={() => setActionMenuId(actionMenuId === user.id ? null : user.id)}
                        className="p-1 hover:bg-dark-700 rounded"
                      >
                        <MoreHorizontal className="w-4 h-4 text-gray-400" />
                      </button>
                      {actionMenuId === user.id && (
                        <div className="absolute right-0 mt-1 w-40 bg-dark-800 rounded-lg shadow-lg border border-dark-700 z-10">
                          <Link
                            to={`/admin/users/${user.id}`}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-dark-700"
                          >
                            <UserCircle className="w-4 h-4" />
                            View Details
                          </Link>
                          {user.is_active ? (
                            <button
                              onClick={() => handleDeactivate(user.id)}
                              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-400 hover:bg-dark-700"
                            >
                              <UserX className="w-4 h-4" />
                              Deactivate
                            </button>
                          ) : (
                            <button
                              onClick={() => handleActivate(user.id)}
                              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-green-400 hover:bg-dark-700"
                            >
                              <UserCheck className="w-4 h-4" />
                              Activate
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-400">
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{user.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <FolderKanban className="w-4 h-4" />
                      <span>{user.project_count} projects assigned</span>
                    </div>
                    {user.last_login && (
                      <div className="flex items-center gap-2 text-gray-400">
                        <Clock className="w-4 h-4" />
                        <span>Last login: {new Date(user.last_login).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-400">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const params = new URLSearchParams(searchParams);
                    params.set('page', (page - 1).toString());
                    setSearchParams(params);
                  }}
                  disabled={page <= 1}
                  className="p-2 bg-dark-800 hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-400" />
                </button>
                <button
                  onClick={() => {
                    const params = new URLSearchParams(searchParams);
                    params.set('page', (page + 1).toString());
                    setSearchParams(params);
                  }}
                  disabled={page >= totalPages}
                  className="p-2 bg-dark-800 hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
                >
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'invitations' && (
        <div className="space-y-3">
          {invitations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No invitations yet</div>
          ) : (
            invitations.map((inv) => (
              <div
                key={inv.id}
                className="bg-dark-900 rounded-xl border border-dark-800 p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    {statusIcon(inv)}
                    <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${
                      inv.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                      inv.status === 'accepted' ? 'bg-green-500/20 text-green-400' :
                      inv.status === 'expired' ? 'bg-gray-500/20 text-gray-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {inv.status}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{inv.full_name || inv.email}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        inv.role === 'admin'
                          ? 'bg-purple-500/20 text-purple-400'
                          : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {inv.role}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">{inv.email}</p>
                    <p className="text-xs text-gray-600">
                      Sent {new Date(inv.created_at).toLocaleDateString()}
                      {inv.inviter_name && ` by ${inv.inviter_name}`}
                    </p>
                  </div>
                </div>

                {inv.status === 'pending' && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleResendInvitation(inv.id)}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-300 hover:text-white bg-dark-800 hover:bg-dark-700 rounded-lg transition-colors"
                    >
                      <Send className="w-3.5 h-3.5" />
                      Resend
                    </button>
                    <button
                      onClick={() => handleCancelInvitation(inv.id)}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-400 hover:text-red-300 bg-dark-800 hover:bg-dark-700 rounded-lg transition-colors"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={() => {
          fetchInvitations();
          setActiveTab('invitations');
        }}
      />
    </div>
  );
}
